/**
 * Admin-only Downloads page: serves admin-protected static documents
 * (PDF + Markdown master) via the backend, with JWT in the Authorization
 * header (handled via fetch + blob, since a plain anchor href cannot send
 * the bearer token).
 */
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, FileText, Download, Loader2, FileCode, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { API, api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import HetznerConfigForm from "../components/HetznerConfigForm";
import SmtpConfigForm from "../components/SmtpConfigForm";

const DOCS = [
  {
    slug: "overview-pdf",
    title: "Novatus Sales Academy — Produkt- & Marktübersicht",
    description:
      "Vollständige 10-Sektionen-Übersicht zur wirtschaftlichen Bewertung und Markt-Einschätzung. Enthält Tech-Stack, Geschäftsmodell, Unit Economics, Wettbewerbsumfeld und Roadmap. Format: PDF, ca. 33 KB.",
    filename: "NovatusAcademy_Overview.pdf",
    icon: FileText,
    mime: "application/pdf",
  },
  {
    slug: "overview-md",
    title: "Selbe Übersicht als Markdown (zum Kopieren in ChatGPT)",
    description:
      "Reines Markdown — perfekt um den Inhalt direkt in ChatGPT, Notion oder ein Pitch-Deck-Tool einzufügen. Format: .md, ca. 22 KB.",
    filename: "NovatusAcademy_Overview.md",
    icon: FileCode,
    mime: "text/markdown",
  },
];

const DownloadsPage = () => {
  const { user } = useAuth();
  const [loadingSlug, setLoadingSlug] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(true);

  // Audio-storage health probe so admins can verify on iPhone (no DevTools needed)
  // whether HETZNER_S3_* secrets are actually loaded on the current backend.
  // Hook must be called unconditionally; the request itself 403s for non-admins
  // and we redirect below before rendering anything sensitive.
  const reloadDiag = () => {
    setDiagLoading(true);
    api.get("/admin/recordings/diagnostics")
      .then(({ data }) => setDiag(data))
      .catch((e) => setDiag({ error: e?.response?.data?.detail || e?.message || "Fehler" }))
      .finally(() => setDiagLoading(false));
  };

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    reloadDiag();
  }, [user]);

  // Strict admin only — direktor/manager/trainer cannot view this page.
  if (user && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  const download = async (doc) => {
    setLoadingSlug(doc.slug);
    try {
      const token = localStorage.getItem("novatus_token");
      const resp = await fetch(`${API}/admin/downloads/${doc.slug}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Give the browser a moment before revoking
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success(`${doc.filename} heruntergeladen`);
    } catch (e) {
      toast.error(e?.message || "Download fehlgeschlagen");
    } finally {
      setLoadingSlug(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="downloads-page">
      <Link
        to="/profil"
        className="hidden sm:inline-flex text-sm text-novatus-text-3 hover:text-novatus-green items-center gap-1 mb-4"
        data-testid="downloads-back-link"
      >
        <ArrowLeft className="w-4 h-4" /> Zurück
      </Link>

      <p className="label-eyebrow text-novatus-green">Interne Dokumente</p>
      <h1 className="font-display font-black text-3xl sm:text-5xl text-white mt-1 leading-tight">Downloads</h1>
      <p className="text-novatus-text-2 mt-3 max-w-2xl">
        Administrative Dokumente zur App. Nur für angemeldete Admins/Direktoren zugänglich.
      </p>

      {/* Audio-Storage Health Card — iPhone-friendly replacement for browser DevTools */}
      <div className="mt-8 nv-card p-5" data-testid="audio-storage-diag">
        <p className="label-eyebrow text-novatus-green">Audio-Speicher Status</p>
        {diagLoading ? (
          <p className="text-sm text-novatus-text-3 mt-3 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Prüfe Hetzner-Konfiguration…
          </p>
        ) : diag?.error ? (
          <div className="mt-3 flex items-start gap-2 text-sm text-amber-300" data-testid="diag-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-400" />
            <span>Diagnose konnte nicht geladen werden: {String(diag.error)}</span>
          </div>
        ) : (
          <>
            <div className={`mt-3 flex items-center gap-2 text-sm font-bold ${diag.configured ? "text-novatus-green" : "text-amber-300"}`} data-testid="diag-summary">
              {diag.configured ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
              {diag.configured ? "Audio-Speicher aktiv" : "Audio-Speicher NICHT konfiguriert"}
            </div>
            <ul className="mt-3 space-y-1.5 text-xs font-mono text-novatus-text-2">
              <li className="flex items-center gap-2" data-testid="diag-endpoint">
                {diag.endpoint ? <CheckCircle2 className="w-3.5 h-3.5 text-novatus-green shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                <span className="text-novatus-text-3">ENDPOINT:</span>
                <span className="break-all">{diag.endpoint || "— (leer)"}</span>
              </li>
              <li className="flex items-center gap-2" data-testid="diag-region">
                {diag.region ? <CheckCircle2 className="w-3.5 h-3.5 text-novatus-green shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                <span className="text-novatus-text-3">REGION:</span>
                <span>{diag.region || "— (leer)"}</span>
              </li>
              <li className="flex items-center gap-2" data-testid="diag-bucket">
                {diag.bucket ? <CheckCircle2 className="w-3.5 h-3.5 text-novatus-green shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                <span className="text-novatus-text-3">BUCKET:</span>
                <span>{diag.bucket || "— (leer)"}</span>
              </li>
              <li className="flex items-center gap-2" data-testid="diag-access">
                {diag.access_key_present ? <CheckCircle2 className="w-3.5 h-3.5 text-novatus-green shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                <span className="text-novatus-text-3">ACCESS_KEY:</span>
                <span>{diag.access_key_present ? "gesetzt" : "— (leer)"}</span>
              </li>
              <li className="flex items-center gap-2" data-testid="diag-secret">
                {diag.secret_key_present ? <CheckCircle2 className="w-3.5 h-3.5 text-novatus-green shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                <span className="text-novatus-text-3">SECRET_KEY:</span>
                <span>{diag.secret_key_present ? "gesetzt" : "— (leer)"}</span>
              </li>
            </ul>
            {!diag.configured && (
              <p className="text-[11px] text-amber-200 mt-3 leading-relaxed">
                💡 Wenn ein oder mehrere Felder „leer" zeigen, sind die Werte im
                Emergent <strong>Secrets-Tab</strong> nicht persistent gespeichert.
                Trage sie unten direkt in unsere Datenbank ein — das umgeht den
                Plattform-Bug komplett.
              </p>
            )}
          </>
        )}
      </div>

      {/* SMTP + Stealth-Admin Diagnostics — same Production secrets-bug class of issue */}
      {diag && !diag.error && (
        <div className="mt-4 nv-card p-5" data-testid="auth-diag">
          <p className="label-eyebrow text-novatus-green">Login & E-Mail Status</p>

          {diag.smtp && (
            <>
              <div className={`mt-3 flex items-center gap-2 text-sm font-bold ${diag.smtp.active && diag.smtp.all_present ? "text-novatus-green" : "text-amber-300"}`}>
                {diag.smtp.active && diag.smtp.all_present ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                {diag.smtp.active && diag.smtp.all_present
                  ? "SMTP aktiv & alle Variablen gesetzt"
                  : "SMTP NICHT vollständig konfiguriert"}
              </div>
              <ul className="mt-3 space-y-1.5 text-xs font-mono text-novatus-text-2">
                <li className="flex items-center gap-2">
                  {diag.smtp.active ? <CheckCircle2 className="w-3.5 h-3.5 text-novatus-green shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                  <span className="text-novatus-text-3">OTP_DELIVERY:</span>
                  <span>{diag.smtp.delivery_mode}</span>
                </li>
                {Object.entries(diag.smtp.vars_present).map(([k, v]) => (
                  <li key={k} className="flex items-center gap-2">
                    {v ? <CheckCircle2 className="w-3.5 h-3.5 text-novatus-green shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                    <span className="text-novatus-text-3">SMTP_{k.toUpperCase()}:</span>
                    <span>{v ? "gesetzt" : "— (leer)"}</span>
                  </li>
                ))}
              </ul>
              {(!diag.smtp.active || !diag.smtp.all_present) && (
                <>
                  <p className="text-[11px] text-amber-200 mt-3 leading-relaxed">
                    💡 Wenn SMTP-Variablen fehlen → entweder im Emergent Secrets-Tab
                    neu eintragen + re-deployen, oder unten direkt in unsere
                    Datenbank speichern (umgeht den Plattform-Bug komplett).
                  </p>
                  <SmtpConfigForm onSaved={reloadDiag} />
                </>
              )}
            </>
          )}

          {diag.stealth_admins?.length > 0 && (
            <>
              <p className="label-eyebrow text-novatus-green mt-5">Stealth-Admin Status</p>
              <ul className="mt-3 space-y-2 text-xs">
                {diag.stealth_admins.map((s, i) => (
                  <li key={i} className="flex items-start gap-2" data-testid={`stealth-admin-${i}`}>
                    {s.healthy ? <CheckCircle2 className="w-4 h-4 text-novatus-green shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />}
                    <div className="font-mono flex-1 min-w-0">
                      <div className="text-novatus-text-2 truncate">{s.expected_email}</div>
                      <div className="text-novatus-text-3 text-[11px]">
                        {s.expected_phone} · role: <span className={s.role === "admin" ? "text-novatus-green" : "text-amber-300"}>{s.role || "—"}</span>
                        {s.is_hidden ? " · hidden ✓" : " · hidden ✗"}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              {diag.stealth_admins.some((s) => !s.healthy) && (
                <p className="text-[11px] text-amber-200 mt-3 leading-relaxed">
                  💡 Wenn eine Stealth-Admin-Zeile rot ist: einfach 1× mit der angezeigten
                  Telefonnummer einloggen — das System setzt die Rolle automatisch zurück
                  auf <code className="text-novatus-green">admin</code>.
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* DB-based fallback config form — visible only when storage is not configured */}
      {diag && !diag.error && !diag.configured && (
        <HetznerConfigForm onSaved={reloadDiag} />
      )}

      <ul className="mt-10 space-y-4">
        {DOCS.map((doc) => {
          const Icon = doc.icon;
          const isLoading = loadingSlug === doc.slug;
          return (
            <li key={doc.slug} className="nv-card p-6 flex flex-col sm:flex-row sm:items-start gap-4" data-testid={`download-item-${doc.slug}`}>
              <div className="w-12 h-12 shrink-0 rounded-xl bg-novatus-green/10 border border-novatus-green/40 flex items-center justify-center">
                <Icon className="w-6 h-6 text-novatus-green" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-display font-bold text-white text-lg leading-tight">{doc.title}</h2>
                <p className="text-sm text-novatus-text-2 mt-2 leading-relaxed">{doc.description}</p>
                <p className="text-[11px] text-novatus-text-3 mt-2 font-mono">{doc.filename}</p>
              </div>
              <button
                onClick={() => download(doc)}
                disabled={isLoading}
                className="btn-green h-10 px-4 rounded-lg inline-flex items-center gap-2 text-sm whitespace-nowrap disabled:opacity-50"
                data-testid={`download-btn-${doc.slug}`}
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {isLoading ? "Lädt …" : "Herunterladen"}
              </button>
            </li>
          );
        })}
      </ul>

      <p className="text-[11px] text-novatus-text-3 mt-8 text-center">
        Die Markdown-Variante kannst du in ChatGPT einfügen mit dem Prompt: <em>„Hier ist meine
        Produktübersicht. Erstelle daraus ein Investor-Pitch-Deck / eine Sales-One-Pager / eine
        Wettbewerbsanalyse."</em>
      </p>
    </div>
  );
};

export default DownloadsPage;
